import { useState, useContext, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { registerKeyHandler, unregisterKeyHandler, setFocusMode, useFlowFormGlobal, FlowFormLeaveContext, useConfirmationModal } from 't-lib-ui/structure';
import { sidebarLeaveHandler } from '../components/LeftSidebarFocus';
import { useLocation, RouterContext, goTo, useParams } from 't-lib-ui/router';
import MainLayout from '../components/MainLayout';
import { requestStartCollapsed } from '../components/ThreeColumnLayout';
import HeadlineList, { HeadlineListHandle } from '../components/HeadlineList';
import PostView, { PostViewHandle } from '../components/PostView';
import { AppState } from '../context/general';
import { ShortcutsContext, ShortcutGroup, GO_HOME_SHORTCUT, ADD_FEED_SHORTCUT, ADD_FEED_KEYS } from '../components/ShortcutsOverlay';
import { usePreferences } from '../connector/preferences';
import FeedTile from '../components/FeedTile';
import { getPublicFeed } from '../connector/api';
import { Spinner } from 't-lib-ui/widgets';
import { FetchError } from 't-lib-ui/connector';
import { useEngineState } from '../utils/useEngineState';
import FeedLoadError from '../components/FeedLoadError';
import { freezePostList } from './frozenPostList';
import type { RestUserFeed } from '../schema-copy/api-schema';
import { getFeedIconUrl } from '../schema-copy/feed-icon';
import FeedQuotaDialog from '../components/FeedQuotaDialog';
import FeedTileSettings from '../components/FeedTileSettings';
import { PauseControls } from '../components/PauseFeedPopup';
import { isSuperuser } from '../schema-copy/capabilities';
import { useEscapeBack } from '../utils/useEscapeBack';
import { requestHomeScrollRestore, requestAddFeedRestore, getPreviewReturnPath, getFeedOpenedFromTile, setFeedOpenedFromTile, getFeedOpenedFromSearch, setFeedOpenedFromSearch } from './homeScroll';

// Maps a special-feed id to the user-preference key that stores its sort/view mode.
// Regular feeds store these on the feed-engine per-feed marker instead.
const SORT_PREF_KEYS = {
  'all-items': 'allItemsSortOrder',
  'starred': 'starredSortOrder',
  'read-later': 'readLaterSortOrder',
} as const;
const VIEW_PREF_KEYS = {
  'all-items': 'allItemsCompactMode',
  'starred': 'starredCompactMode',
  'read-later': 'readLaterCompactMode',
} as const;
type SpecialFeed = keyof typeof SORT_PREF_KEYS;
// 'single' (single-column) is persisted as the legacy 'compact' preference value /
// 'c' read-marker letter; the mapping happens in readViewPref/viewMode/applyViewMode.
type ViewMode = 'full' | 'single' | 'list';

const specialFeedSortKey = (sf: string) =>
  (SORT_PREF_KEYS as Record<string, typeof SORT_PREF_KEYS[SpecialFeed]>)[sf];
const specialFeedViewKey = (sf: string) =>
  (VIEW_PREF_KEYS as Record<string, typeof VIEW_PREF_KEYS[keyof typeof VIEW_PREF_KEYS]>)[sf];

const READER_SHORTCUTS: ShortcutGroup[] = [
  {
    title: 'Navigation',
    shortcuts: [
      GO_HOME_SHORTCUT,
      ADD_FEED_SHORTCUT,
      { keys: ['Alt', 'n'], description: 'Next feed' },
      { keys: ['Alt', 'c'], description: 'Focus options menu' },
    ],
  },
  {
    title: 'Reading',
    shortcuts: [
      { keys: ['j', 'n'], description: 'Next post' },
      { keys: ['k', 'p'], description: 'Previous post' },
      { keys: ['Enter'], description: 'Open selected post (when nothing focused)' },
      { keys: ['Space'], description: 'Scroll down' },
      { keys: ['Shift', 'Space'], description: 'Scroll up' },
    ],
  },
  {
    title: 'Actions',
    shortcuts: [
      { keys: ['s'], description: 'Star / unstar' },
      { keys: ['m'], description: 'Lock / unlock (keep unread)' },
      { keys: ['r'], description: 'Toggle read later' },
      { keys: ['Alt', 'a'], description: 'Mark all read' },
      { keys: ['Alt', 'k'], description: 'Keep all unread' },
      { keys: ['Alt', 's'], description: 'Skip feed (keep all unread, go to next feed)' },
      { keys: ['Alt', 'f'], description: 'Focus feeds' },
    ],
  },
];

// Set the first time the user navigates posts with the keyboard (j/k/n/p). Read on the
// Home page to surface a one-time "keyboard user" tips card. Kept in localStorage (a
// soft, per-device signal) rather than synced preferences.
export const READER_KEYS_USED_KEY = 'downread-reader-keys-used';
function markReaderKeysUsed() {
  try { localStorage.setItem(READER_KEYS_USED_KEY, '1'); } catch { /* ignore quota/availability */ }
}

const Main: React.FC = () => {
  useFlowFormGlobal('.flowForm.mainForm');
  const appState = useContext(AppState);
  const [, updatePreferences] = usePreferences();
  const routerCtx = useContext(RouterContext);
  const location = useLocation();
  const { id: feedIdFromUrl, folder: folderParam } = useParams<{ id?: string; folder?: string }>();

  // /a/new/<folder> limits the all-items view to one folder. The router doesn't decode
  // URL segments, so decode here (fall back to the raw value on malformed escapes).
  const selectedFolder = useMemo(() => {
    if (!folderParam) return undefined;
    try { return decodeURIComponent(folderParam); } catch { return folderParam; }
  }, [folderParam]);

  // Derive selectedFeed from URL. Special feeds ('all-items', 'starred', 'read-later')
  // get their sort/view mode from userPreferences; regular feeds get it from the
  // feed-engine per-feed marker.
  const selectedFeed = feedIdFromUrl ? feedIdFromUrl
    : location.pathName == '/a/starred' ? 'starred'
    : location.pathName == '/a/read-later' ? 'read-later'
    : 'all-items';
  const isSpecialFeed = !!specialFeedSortKey(selectedFeed);

  const readSortPref = (sf: string): boolean | undefined => {
    const key = specialFeedSortKey(sf);
    return key ? (appState.state.userPreferences[key] ?? 'newest') == 'newest' : undefined;
  };
  const readViewPref = (sf: string): ViewMode | undefined => {
    const key = specialFeedViewKey(sf);
    if (!key) return undefined;
    const v = appState.state.userPreferences[key];
    return v == 'compact' ? 'single' : v == 'list' ? 'list' : 'full';
  };

  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [postList, setPostList] = useState<[string, number][]>([]);
  const [scrollToPost, setScrollToPost] = useState<[string, number] | undefined>();
  const [sortNewestFirst, setSortNewestFirst] = useState(() => readSortPref(selectedFeed) ?? true);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [showAllTemporarily, setShowAllTemporarily] = useState(false);
  // Browse-only ('a') feed showing only down to the last unread post, with the
  // older (read) posts hidden behind a "Show more" button.
  const [browseTruncated, setBrowseTruncated] = useState(false);
  const [feedsVersion, setFeedsVersion] = useState(0);
  const [compactModeVersion, setCompactModeVersion] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1920);
  const isMobileWidth = windowWidth <= 750;
  const [pendingFeedNavigation, setPendingFeedNavigation] = useState<string | null>(null);
  const unreadSnapshotRef = useRef<Set<number>>(new Set());
  const prevSelectedFeedRef = useRef<string>('');
  const initialHashRef = useRef(location.hash);
  const headlineListRef = useRef<HeadlineListHandle>(null);
  const postViewRef = useRef<PostViewHandle>(null);
  const hashUpdateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevListKeyRef = useRef('');
  const prevViewKeyRef = useRef('');
  // Navigation cursor: the authoritative post index j/k step from. Updated
  // synchronously on each keypress so rapid presses chain even while a scroll
  // animates. When idle it follows the post the user actually sees (the blue
  // border, via PostView.getCurrentVisible) so Space/manual scroll then j is
  // correct. pendingNavRef holds the post we are programmatically scrolling to;
  // while set, transient mid-scroll visible-post changes must not move the
  // cursor (only header/hash/headline display follows them).
  const navCursorRef = useRef(0);
  const pendingNavRef = useRef<[string, number] | null>(null);
  const pendingNavTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [unreadRefreshCounter, setUnreadRefreshCounter] = useState(0);
  const [allItemsLoading, setAllItemsLoading] = useState(true);
  const [unsubFeedInfo, setUnsubFeedInfo] = useState<Awaited<ReturnType<typeof getPublicFeed>> | null>(null);
  const [showQuotaDialog, setShowQuotaDialog] = useState(false);
  // Refs for keyboard handlers to avoid stale closures
  const postListRef = useRef(postList);
  const showUnreadOnlyRef = useRef(showUnreadOnly);
  postListRef.current = postList;
  showUnreadOnlyRef.current = showUnreadOnly;
  const selectedFeedRef = useRef(selectedFeed);
  selectedFeedRef.current = selectedFeed;

  // True while the feed engine is still loading initial data. Also covers the
  // special lists (starred/read-later/all-items): their entries come from engine
  // data too, so building/rendering them before the engine is ready would show a
  // bogus "no posts" empty state on direct URL opens.
  // A failed load (ERROR) is handled separately in the render below — it must
  // show an error panel, never an empty "no posts" state.
  const engineState = useEngineState(appState.feedEngine);
  const feedsStillLoading = engineState == 'OFF' || engineState == 'LOADING';
  // Gate for the special-list fetch effects below. Collapsed into one boolean so
  // that only blocked<->unblocked flips re-run them: polls (READY->UPDATING->READY),
  // pause/resume and offline transitions must NOT refetch the lists — the refetch
  // bumps feedsVersion, which rebuilds the post list and resets the scroll position
  // while the user is reading.
  const engineBlocked = feedsStillLoading || engineState == 'ERROR';

  // The unsubscribed-feed view is "sticky" per feed id: once we entered the page on an
  // unsubscribed feed, we stay in the FeedTile-preview layout even after the user clicks Follow.
  // Following only turns the read-only preview into a regular reading view in place (see
  // isReadOnlyPreview). The latch resets when the URL points at a different feed, or when the
  // followed feed's view mode or sort order is changed (leaveTileView).
  const [stuckTileViewFeedId, setStuckTileViewFeedId] = useState<string | null>(null);

  const isCurrentlyUnsubscribed = useMemo(() => {
    if (!feedIdFromUrl || feedsStillLoading) return false;
    return !appState.feedEngine.getFeeds().some(f => f.id == feedIdFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedIdFromUrl, feedsStillLoading, appState.feedEngine, feedsVersion, compactModeVersion]);

  useEffect(() => {
    if (stuckTileViewFeedId && stuckTileViewFeedId != feedIdFromUrl)
      setStuckTileViewFeedId(null);
    else if (isCurrentlyUnsubscribed && stuckTileViewFeedId != feedIdFromUrl)
      setStuckTileViewFeedId(feedIdFromUrl ?? null);
  }, [feedIdFromUrl, isCurrentlyUnsubscribed, stuckTileViewFeedId]);

  const isUnsubscribedFeed = !!feedIdFromUrl && !feedsStillLoading && stuckTileViewFeedId == feedIdFromUrl;
  // A followed feed whose FeedTile was toggled on by clicking the feed name in the header. Kept per
  // feed id, so it goes away when another feed is selected.
  const [tileShownFeedId, setTileShownFeedId] = useState<string>();
  // A paused feed shows its tile by default; this records that the user hid it anyway.
  const [tileHiddenFeedId, setTileHiddenFeedId] = useState<string>();
  // The preview is read-only until the feed is followed from its tile; from then on the same
  // layout (tile on top, single-post mode) gets the reading controls and shows the unread posts.
  const isReadOnlyPreview = isUnsubscribedFeed && isCurrentlyUnsubscribed;

  // A followed feed opened from a tile (Home, Add Feed) gets the preview's back button as well. Taken
  // over from homeScroll once per navigation, and kept only while that feed stays selected.
  const [tileOriginFeedId, setTileOriginFeedId] = useState(getFeedOpenedFromTile);
  // A feed picked from the search suggestions has no back button (see homeScroll).
  const [searchOriginFeedId, setSearchOriginFeedId] = useState(getFeedOpenedFromSearch);
  useEffect(() => {
    const opened = getFeedOpenedFromTile();
    setFeedOpenedFromTile(undefined);
    setTileOriginFeedId(prev => opened ?? (prev == feedIdFromUrl ? prev : undefined));
    const fromSearch = getFeedOpenedFromSearch();
    setFeedOpenedFromSearch(undefined);
    setSearchOriginFeedId(prev => fromSearch ?? (prev == feedIdFromUrl ? prev : undefined));
  }, [feedIdFromUrl]);
  const showBack = (!!feedIdFromUrl && searchOriginFeedId != feedIdFromUrl)
    && (isReadOnlyPreview || tileOriginFeedId == feedIdFromUrl);
  const handleBack = showBack ? () => {
    // Back to where the feed was opened from: a Discover category page, Add Feed or Home (the latter two restoring their scroll position).
    const returnPath = getPreviewReturnPath();
    if (!returnPath) requestHomeScrollRestore();
    else if (returnPath.startsWith('/a/add')) requestAddFeedRestore();
    goTo(routerCtx, returnPath ?? '/a/home');
  } : undefined;
  useEscapeBack('main-back', handleBack);

  // Detect when viewing a newly-subscribed feed whose posts haven't arrived yet.
  // The subscribe result says whether the server is actually fetching the feed
  // (isRefreshing) and kicks off a poll; while we wait, show a busy indicator
  // instead of an empty white view.
  const feedLoadingPosts = useMemo(() => {
    if (!feedIdFromUrl || feedsStillLoading || isReadOnlyPreview) return false;
    const feed = appState.feedEngine.getFeed(feedIdFromUrl);
    if (!feed || feed.postCount > 0) return false;
    return appState.feedEngine.isRefreshing(feedIdFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedIdFromUrl, feedsStillLoading, isReadOnlyPreview, appState.feedEngine, feedsVersion, compactModeVersion]);

  // Layout of a feed you don't follow: one post at a time, or List from its header menu. Only kept for
  // the visit (nothing is stored for a feed you don't follow), and carried over when following from it.
  const [previewViewMode, setPreviewViewMode] = useState<ViewMode>('single');
  useEffect(() => setPreviewViewMode('single'), [feedIdFromUrl]);

  // Resolve view mode: user preference for special feeds, feed-engine marker for regular feeds.
  const viewMode: ViewMode = useMemo(() => {
    // The tile-preview layout (also right after following from it).
    if (isUnsubscribedFeed) return previewViewMode;
    const pref = readViewPref(selectedFeed);
    if (pref) return pref;
    const c = appState.feedEngine.getCompactMode(selectedFeed);
    return c == 'c' ? 'single' : c == 'l' ? 'list' : 'full';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, isUnsubscribedFeed, previewViewMode, appState.feedEngine, compactModeVersion, appState.state.userPreferences.allItemsCompactMode, appState.state.userPreferences.starredCompactMode, appState.state.userPreferences.readLaterCompactMode]);
  const viewModeRef = useRef(viewMode);
  viewModeRef.current = viewMode;

  // Changing the view mode or sort order of a feed just followed from its preview tile leaves the
  // sticky preview layout: the feed becomes a regular followed feed, keeping its tile on top.
  const leaveTileView = useCallback(() => {
    if (!isUnsubscribedFeed) return;
    setStuckTileViewFeedId(null);
    setTileShownFeedId(selectedFeed);
  }, [isUnsubscribedFeed, selectedFeed]);

  // Persist a new view mode for the current feed, then reset scroll position.
  const applyViewMode = useCallback((next: ViewMode) => {
    if (isReadOnlyPreview) {
      setPreviewViewMode(next);
      setCurrentPostIndex(0);
      return;
    }
    const key = specialFeedViewKey(selectedFeed);
    if (key) {
      updatePreferences({ [key]: next == 'single' ? 'compact' : next } as Partial<Parameters<typeof updatePreferences>[0]>);
    } else {
      appState.feedEngine.setCompactMode(selectedFeed, next == 'single' ? 'c' : next == 'list' ? 'l' : 'f');
      setCompactModeVersion(v => v + 1);
    }
    leaveTileView();
    setCurrentPostIndex(0);
  }, [isReadOnlyPreview, selectedFeed, appState.feedEngine, updatePreferences, leaveTileView]);

  // At mobile width there's no HeadlineList, so 'full' and 'single' render
  // identically — collapse them into a single "one-column" choice. 'full' is
  // shown as one-column without rewriting the stored preference; it only becomes
  // 'single' once the user toggles to list and back.
  const effectiveViewMode: ViewMode = isMobileWidth && viewMode == 'full' ? 'single' : viewMode;

  // Cycle view mode: full -> single -> list -> full.
  // At mobile width there's only one-column <-> list.
  const handleCycleViewMode = useCallback(() => {
    if (isMobileWidth) return applyViewMode(effectiveViewMode == 'list' ? 'single' : 'list');
    applyViewMode(viewMode == 'full' ? 'single' : viewMode == 'single' ? 'list' : 'full');
  }, [viewMode, effectiveViewMode, isMobileWidth, applyViewMode]);

  // Set view mode directly (no-op if unchanged).
  const handleSetViewMode = useCallback((next: ViewMode) => {
    if (next != viewMode) applyViewMode(next);
  }, [viewMode, applyViewMode]);

  // Navigate to feed URL when selecting a feed.
  // On mobile, ensure the next page's left sidebar starts collapsed — navigations
  // triggered from PostView/HeadlineList buttons (e.g. "Find more feeds", "Similar Feeds")
  // don't go through the sidebar, so they'd otherwise land on the new page with
  // the drawer open.
  const handleSelectFeed = useCallback((feedId: string) => {
    if (hashUpdateTimerRef.current) { clearTimeout(hashUpdateTimerRef.current); hashUpdateTimerRef.current = null; }
    // Folder target ('folder:<name>', from the folder unread view's Next Folder
    // button): selectedFeed stays 'all-items', so the pending-navigation latch
    // (which is cleared by selectedFeed catching up) must not engage; the listKey
    // effect resets the post index when selectedFolder changes.
    if (feedId.startsWith('folder:')) {
      setPendingFeedNavigation(null);
      if (window.innerWidth <= 750) requestStartCollapsed();
      goTo(routerCtx, `/a/new/${encodeURIComponent(feedId.slice(7))}`);
      return;
    }
    const current = selectedFeedRef.current;
    const isCrossFeedNavigation = feedId != current;
    setPendingFeedNavigation(isCrossFeedNavigation ? feedId : null);
    if (isCrossFeedNavigation) {
      setCurrentPostIndex(0);
      setScrollToPost(undefined);
    }
    // Same-page feed changes are handled by the layout's read-mode effect; only the
    // mobile drawer needs the cross-page collapse flag here (e.g. navigating to Add feed).
    if (window.innerWidth <= 750) requestStartCollapsed();
    if (feedId == 'all-items')
      goTo(routerCtx, '/a/new');
    else if (feedId == 'home')
      goTo(routerCtx, '/a/home');
    else if (feedId == 'starred' || feedId == 'read-later' || feedId == 'add')
      goTo(routerCtx, `/a/${feedId}`);
    else if (feedId.startsWith('add/'))
      goTo(routerCtx, `/a/${feedId}`);
    else
      goTo(routerCtx, `/a/feed/${feedId}`);
  }, [routerCtx]);

  useEffect(() => {
    if (pendingFeedNavigation == null) return;
    if (pendingFeedNavigation == selectedFeed)
      setPendingFeedNavigation(null);
  }, [pendingFeedNavigation, selectedFeed]);

  // Update URL hash silently when post changes (debounced to avoid lag on rapid key presses)
  const updateHash = useCallback((postNumber: number) => {
    if (!feedIdFromUrl) return;
    if (hashUpdateTimerRef.current) clearTimeout(hashUpdateTimerRef.current);
    hashUpdateTimerRef.current = setTimeout(() => {
      goTo(routerCtx, `/a/feed/${feedIdFromUrl}#${postNumber}`, 'silent');
    }, 150);
  }, [routerCtx, feedIdFromUrl]);

  // The post j/k step from. When no programmatic navigation is in flight, this
  // is the post the user actually sees (PostView's blue-border post), so it is
  // correct after a manual scroll or a Space page. While navigating, it is the
  // synchronously-advanced cursor so rapid presses chain through the in-flight
  // scroll instead of restepping from a frozen/transient position.
  const resolveCurrentIndex = useCallback(() => {
    const posts = postListRef.current;
    if (!pendingNavRef.current) {
      const v = postViewRef.current?.getCurrentVisible();
      if (v) {
        const i = posts.findIndex(([f, n]) => f == v[0] && n == v[1]);
        if (i >= 0) return i;
      }
    }
    return Math.min(Math.max(navCursorRef.current, 0), Math.max(posts.length - 1, 0));
  }, []);

  const navigateToIndex = useCallback((index: number) => {
    const posts = postListRef.current;
    const target = posts[index];
    if (!target) return;
    navCursorRef.current = index;
    pendingNavRef.current = [target[0], target[1]];
    if (pendingNavTimerRef.current) clearTimeout(pendingNavTimerRef.current);
    // Safety: if the target's visible-post change is never reported (e.g. it
    // was already on screen so no scroll fires), stop blocking cursor-follow.
    pendingNavTimerRef.current = setTimeout(() => {
      pendingNavRef.current = null;
      pendingNavTimerRef.current = null;
    }, 1200);
    setCurrentPostIndex(index);
    setScrollToPost([target[0], target[1]]);
    updateHash(target[1]);
    headlineListRef.current?.selectPost(target[0], target[1]);
  }, [updateHash]);

  const handleHeadlineSelect = useCallback((feedId: string, postNumber: number) => {
    const index = postListRef.current.findIndex(([fid, pn]) => fid == feedId && pn == postNumber);
    if (index >= 0) navigateToIndex(index);
  }, [navigateToIndex]);

  // Scroll to and focus the end-of-list action button (Mark all read / Next Feed),
  // shared by the keyboard nav (j/Space at the end) and the on-screen nav buttons.
  const focusEndOfListAction = useCallback(() => {
    const findBtn = (root: string) => {
      const btns = Array.from(document.querySelectorAll<HTMLElement>(`${root} .markAllReadBottomButton.flowFocussable:not([disabled])`));
      return btns.find(el => el.offsetParent && el.closest('.endOfUnreadNextFeed'))
        ?? btns.find(el => el.offsetParent);
    };
    const btn = findBtn('.postView') ?? findBtn('.headlineList');
    // Scroll the button's own scroll ancestor — not just the first visible
    // .postsContainer / .headlineItems in DOM order, which in full-view
    // mode is the HeadlineList's container, not the PostView's.
    const container = btn?.closest('.postsContainer, .headlineItems') as HTMLElement | null;
    // The post container is driven by PostRenderer's own rAF scroll loop, the same
    // mechanism every post-to-post step uses. A native smooth scrollTo here let the
    // renderer treat the in-flight animation as a manual scroll (trimming, height
    // compensation via scrollTop writes), which on iOS Safari's async scrolling reads
    // a stale scrollTop mid-animation and jumps the view back up into the previous post.
    const rendererScrolled = container?.classList.contains('postsContainer') && postViewRef.current?.scrollToBottom();
    if (container && !rendererScrolled) {
      // PostRenderer's scroll state machine may still be in its 'watching'
      // phase from a prior navigation (lasts up to WATCH_DURATION=5s) and
      // would re-pin scrollTop, fighting the smooth scroll below.
      container.dispatchEvent(new Event('cancelscroll'));
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    }
    // preventScroll: a focus()-driven scroll-into-view would race the
    // smooth scrollTo above, and on repeat presses btn.focus() is a no-op
    // (already focused), so we can't rely on it as the scroll mechanism.
    if (btn) { setFocusMode('visible'); btn.focus({ preventScroll: true }); }
  }, []);

  // The single source of truth for post-to-post navigation, used by both the
  // keyboard (j/k/n/p) and the on-screen nav buttons (footer arrows, side
  // gutters, floating ScrollNavBtn) so they behave identically. Both resolve the
  // anchor via resolveCurrentIndex()/getCurrentVisible() rather than a stale
  // currentPostIndex prop, which is what made the buttons "miss" the target.
  const navNext = useCallback(() => {
    if (viewModeRef.current == 'list') {
      // listNavigate returns false at the list boundary — fall through to the same
      // end-of-list focus behavior used in other view modes.
      if (postViewRef.current?.listNavigate(1)) return;
      focusEndOfListAction();
      return;
    }
    // Preview of an unfollowed feed: its FeedTile above the posts counts as the item before the first.
    if (postViewRef.current?.nextFromTopContent(20)) return;
    const posts = postListRef.current;
    const base = resolveCurrentIndex();
    if (base >= posts.length - 1) {
      focusEndOfListAction();
      return;
    }
    navigateToIndex(base + 1);
  }, [resolveCurrentIndex, navigateToIndex, focusEndOfListAction]);

  const navPrev = useCallback(() => {
    if (viewModeRef.current == 'list') {
      postViewRef.current?.listNavigate(-1);
      return;
    }
    // First 'up' when scrolled into the current post returns to its top; only a
    // subsequent 'up' (within 20px of the top) goes to the previous post.
    if (postViewRef.current?.prevPostOrScrollToTop(20)) return;
    navigateToIndex(Math.max(resolveCurrentIndex() - 1, 0));
  }, [resolveCurrentIndex, navigateToIndex]);

  const handleVisiblePostChange = useCallback((feedId: string, postNumber: number) => {
    const posts = postListRef.current;
    const index = posts.findIndex(([fid, pn]) => fid == feedId && pn == postNumber);
    if (index < 0) return;
    const pending = pendingNavRef.current;
    if (pending) {
      // We reached the post we navigated to: stop blocking and resync cursor.
      if (pending[0] == feedId && pending[1] == postNumber) {
        pendingNavRef.current = null;
        if (pendingNavTimerRef.current) { clearTimeout(pendingNavTimerRef.current); pendingNavTimerRef.current = null; }
        navCursorRef.current = index;
      }
      // Reflect the transient post for header/hash/headline, but don't let a
      // mid-scroll position move the navigation cursor off the chained target.
      setCurrentPostIndex(index);
      updateHash(postNumber);
      headlineListRef.current?.selectPost(feedId, postNumber);
      return;
    }
    // Manual scroll or Space page: j/k follow exactly what the user sees.
    navCursorRef.current = index;
    setCurrentPostIndex(index);
    updateHash(postNumber);
    headlineListRef.current?.selectPost(feedId, postNumber);
  }, [updateHash]);

  // Re-initialize sort order when selectedFeed changes: special feeds read from
  // userPreferences; regular feeds use their feed-engine sort order, defaulting to
  // newest-first when the feed is in automatic mode.
  useEffect(() => {
    const pref = readSortPref(selectedFeed);
    if (pref != undefined) { setSortNewestFirst(pref); return; }
    const order = appState.feedEngine.getSortOrder(selectedFeed);
    if (order == 'A')
      setSortNewestFirst(true);
    else
      setSortNewestFirst(order != 'O');
  }, [selectedFeed, appState.feedEngine, compactModeVersion, appState.state.userPreferences.starredSortOrder, appState.state.userPreferences.readLaterSortOrder, appState.state.userPreferences.allItemsSortOrder]);

  const handleSortChange = useCallback((newestFirst: boolean) => {
    setSortNewestFirst(newestFirst);
    const key = specialFeedSortKey(selectedFeed);
    if (key)
      updatePreferences({ [key]: newestFirst ? 'newest' : 'oldest' } as Partial<Parameters<typeof updatePreferences>[0]>);
    else if (selectedFeed)
      appState.feedEngine.setSortOrder(selectedFeed, newestFirst ? 'N' : 'O');
    leaveTileView();
  }, [selectedFeed, appState.feedEngine, updatePreferences, leaveTileView]);

  // Track window width for mobile detection
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track marker/view changes from feed-engine (initial load + poll marker updates)
  // without forcing post list refreshes. This keeps direct URL opens in sync with
  // compact/sort marker state while preserving stable scroll behavior.
  //
  // Also warm the next logical feed after each poll: when exactly one (non-current)
  // feed has unread posts, that's unambiguously where the user will go next, so
  // prefetch its first posts to make the switch feel instant.
  useEffect(() => {
    const unregister = appState.feedEngine.onFeedsChange(() => {
      setCompactModeVersion(v => v + 1);

      const fe = appState.feedEngine;
      const current = selectedFeedRef.current;

      // Re-run the postList effect on every marker reconciliation for the
      // currently selected feed. We need this even when postList is non-empty
      // and even when the marker's unreadCount didn't change: a server marker
      // push can silently CORRECT the bitmap (e.g. unset a bit that incorrectly
      // matched a now-correct count) — same count, different read set. Without
      // a bump, Main's frozen snapshot would still reflect the pre-push bitmap
      // and the user gets stuck on "All caught up" until the next poll happens
      // to satisfy `listEmpty`. setPostList is identity-deduped below, so when
      // nothing actually changed this is a cheap no-op.
      if (current) {
        if (current == 'starred' || current == 'read-later') {
          if (!postListRef.current.length) setFeedsVersion(v => v + 1);
        } else if (!specialFeedSortKey(current)) {
          const f = fe.getFeed(current);
          if (f && f.postCount > 0) setFeedsVersion(v => v + 1);
        }
      }

      let onlyUnread: string | undefined;
      for (const f of fe.getFeeds()) {
        if (f.id == current) continue;
        if (fe.getUnreadCount(f.id) <= 0) continue;
        if (onlyUnread) { onlyUnread = undefined; break; }
        onlyUnread = f.id;
      }
      if (onlyUnread) fe.preloadFeed(onlyUnread);
    });
    return unregister;
  }, [appState.feedEngine]);

  // Bump feedsVersion on any unread change for the currently selected feed so
  // the layout effect re-runs and extends the snapshot with newly-unread posts.
  // Without this, a snapshot frozen at navigation time would never see posts
  // added by a poll or marked unread elsewhere — leaving the user stuck on
  // "All caught up" while the engine knows there are unread posts.
  useEffect(() => {
    const unregister = appState.feedEngine.onUnreadChange((feedId) => {
      if (feedId === selectedFeedRef.current)
        setFeedsVersion(v => v + 1);
    });
    return unregister;
  }, [appState.feedEngine]);

  // Fetch unread feed when entering all-items, changing sort, or on explicit refresh.
  // Separate from the main useEffect to avoid infinite loops (feedsVersion is NOT a dep here).
  useEffect(() => {
    if (selectedFeed != 'all-items') {
      setAllItemsLoading(false);
      return;
    }
    // Engine not ready: fetchUnreadFeed would no-op (no auth). Keep the loading
    // state; the effect re-runs when engineBlocked flips. ERROR renders the
    // error panel instead of this page's content, so don't fetch either.
    if (engineBlocked) return;
    setAllItemsLoading(true);
    const sortOrder = sortNewestFirst ? 'newestFirst' : 'oldestFirst' as const;
    appState.feedEngine.clearUnreadFeedCache(sortOrder, selectedFolder);
    appState.feedEngine.fetchUnreadFeed(sortOrder, undefined, selectedFolder).then(() => {
      setFeedsVersion(v => v + 1);
    }).catch(e => {
      // The folder was deleted or renamed — leave the dead URL for the plain unread view.
      if (selectedFolder != undefined && e instanceof FetchError && e.status == 404)
        return goTo(routerCtx, '/a/new');
      // A failing /api/unread (e.g. 500) must not crash the page; the list simply
      // stays empty-but-not-loading and the next refresh/sort change retries.
      console.error('Failed to fetch unread feed:', e);
    }).finally(() => setAllItemsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, selectedFolder, sortNewestFirst, unreadRefreshCounter, engineBlocked, appState.feedEngine]);

  // Fetch starred/read-later lists to get feed metadata for non-subscribed feeds.
  // Waits for the engine: before it's ready the fetch would no-op (no auth), and
  // on direct URL opens this effect must re-run once loading finishes.
  useEffect(() => {
    if (engineBlocked) return;
    if (selectedFeed == 'starred') {
      const sortOrder = sortNewestFirst ? 'newestFirst' : 'oldestFirst' as const;
      appState.feedEngine.fetchStarredList(undefined, sortOrder).then(() => {
        setFeedsVersion(v => v + 1);
      }).catch(e => console.error('Failed to fetch starred list:', e));
    } else if (selectedFeed == 'read-later') {
      const sortOrder = sortNewestFirst ? 'newestFirst' : 'oldestFirst' as const;
      appState.feedEngine.fetchReadLaterList(undefined, sortOrder).then(() => {
        setFeedsVersion(v => v + 1);
      }).catch(e => console.error('Failed to fetch read-later list:', e));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, sortNewestFirst, engineBlocked, appState.feedEngine]);

  // Fetch catalog data for unsubscribed feeds
  useEffect(() => {
    if (!isUnsubscribedFeed || !feedIdFromUrl) {
      setUnsubFeedInfo(null);
      return;
    }
    let cancelled = false;
    // getPublicFeed works for anonymous users too (public endpoint), so don't gate on auth —
    // otherwise unsubscribing an open feed in anon mode leaves the preview column blank.
    getPublicFeed(feedIdFromUrl, appState.state.authData).then(feed => {
      if (cancelled) return;
      // A friendlyId in the URL (shared links): continue under the real id, which may be followed.
      if (feed.id != feedIdFromUrl)
        return goTo(routerCtx, `/a/feed/${feed.id}`, 'replace');
      // Let the post renderer resolve the feed (CDN location etc.) although it isn't followed.
      appState.feedEngine.registerFeedInfo(feed as RestUserFeed);
      setUnsubFeedInfo(feed);
      setFeedsVersion(v => v + 1); // header name/icon come from the engine's feed info
    }).catch((err: unknown) => console.error('Error loading unsubscribed feed info:', err));
    return () => { cancelled = true; };
  }, [isUnsubscribedFeed, feedIdFromUrl, appState.state.authData, routerCtx]);

  // Build post tuples when selected feed changes (no fetch needed - renderers lazy-load)
  useLayoutEffect(() => {
    if (feedsStillLoading) return;

    // Only reset post index when the list structure actually changes
    // (not on data refreshes like starListsVersion from polls/star toggles).
    // viewKey is what the user picked (feed, sort, filter); a feedsVersion-only change is a data
    // refresh (poll, marker reconciliation) and must not move a regular feed's reading position.
    const viewKey = `${selectedFeed}|${selectedFolder ?? ''}|${sortNewestFirst}|${showUnreadOnly}|${showAllTemporarily}|${isReadOnlyPreview}`;
    const listKey = `${viewKey}|${feedsVersion}`;
    const listStructureChanged = listKey != prevListKeyRef.current;
    const viewChanged = viewKey != prevViewKeyRef.current;
    prevListKeyRef.current = listKey;
    prevViewKeyRef.current = viewKey;
    const isRegularFeed = !['starred', 'read-later', 'all-items'].includes(selectedFeed);
    if (listStructureChanged && (viewChanged || !isRegularFeed || !postListRef.current.length)) {
      setCurrentPostIndex(0);
      setScrollToPost(undefined);
    }

    if (selectedFeed == 'starred' || selectedFeed == 'read-later') {
      const entries = selectedFeed == 'starred'
        ? appState.feedEngine.getStarredEntries()
        : appState.feedEngine.getReadLaterEntries();
      let posts: [string, number][] = entries.map(entry => {
        const dashIdx = entry.lastIndexOf('-');
        return [entry.substring(0, dashIdx), parseInt(entry.substring(dashIdx + 1), 10)];
      });
      if (!sortNewestFirst) posts = posts.reverse();
      setPostList(prev => {
        if (prev.length == posts.length && prev.every(([f, n], i) => f == posts[i][0] && n == posts[i][1]))
          return prev;
        return posts;
      });
      return;
    }
    if (selectedFeed == 'all-items') {
      const sortOrder = sortNewestFirst ? 'newestFirst' : 'oldestFirst';
      const cached = appState.feedEngine.getUnreadFeedEntries(sortOrder as 'newestFirst' | 'oldestFirst', selectedFolder);
      if (cached) {
        const posts: [string, number][] = cached.map(entry => {
          const dashIdx = entry.lastIndexOf('-');
          return [entry.substring(0, dashIdx), parseInt(entry.substring(dashIdx + 1), 10)];
        });
        setPostList(prev => {
          if (prev.length == posts.length && prev.every(([f, n], i) => f == posts[i][0] && n == posts[i][1]))
            return prev;
          return posts;
        });
      } else {
        setPostList([]);
      }
      return;
    }

    if (isReadOnlyPreview) {
      // Unfollowed feed preview: its newest posts, newest first (read-only, see PostView 'preview').
      const postCount = unsubFeedInfo?.postCount ?? 0;
      const posts = Array.from({ length: Math.min(postCount, 1000) }, (_, i): [string, number] => [selectedFeed, postCount - 1 - i]);
      // Not recorded as the current feed: once the user follows it from the tile, the regular
      // branch below treats it as freshly opened (unread snapshot, unread-only view).
      prevSelectedFeedRef.current = '';
      setPostList(prev => prev.length == posts.length && prev.every(([f, n], i) => f == posts[i][0] && n == posts[i][1]) ? prev : posts);
      return;
    }

    // Regular feeds: skip rebuild when only star data changed
    if (!listStructureChanged) return;

    const feed = appState.feedEngine.getFeed(selectedFeed);
    if (!feed || !feed.postCount) {
      // TEMP DEBUG (remove): postCount missing/0 while the engine marker still
      // reports unread → we blank the list.
      if (appState.feedEngine.getUnreadCount(selectedFeed) > 0)
        console.warn('[EMPTY-FEED-BUG] postCount falsy but engine reports unread>0', {
          feed: selectedFeed,
          hasFeed: !!feed,
          barePostCount: feed?.postCount,
          engineUnread: appState.feedEngine.getUnreadCount(selectedFeed),
          engineState: appState.feedEngine.getState(),
        });
      setPostList([]);
      return;
    }

    const count = Math.min(feed.postCount, 1000);
    const startPost = feed.postCount - count; // oldest post number to include

    // On feed change, snapshot unread state and auto-detect mode
    const feedChanged = prevSelectedFeedRef.current != selectedFeed;
    prevSelectedFeedRef.current = selectedFeed;

    const buildUnreadSnapshot = () => {
      const readSet = appState.feedEngine.getReadForFeed(selectedFeed);
      const unread = new Set<number>();
      for (let i = startPost; i < feed.postCount; i++)
        if (!readSet.has(i)) unread.add(i);
      return unread;
    };

    // A paused feed has nothing unread: it always lists all its posts.
    const paused = appState.feedEngine.isFeedPaused(selectedFeed);
    let effectiveUnreadOnly = showUnreadOnly && !paused;
    if (paused && showUnreadOnly) setShowUnreadOnly(false);
    if (feedChanged) {
      setShowAllTemporarily(false);
      const unread = buildUnreadSnapshot();
      unreadSnapshotRef.current = unread;
      // Drift detector: the engine reports unread posts but the bitmap says
      // otherwise (fresh snapshot came out empty). This is the "empty feed"
      // bug — the local marker bitmap has silently drifted from the engine's
      // unreadCount. Force a poll so parseServerMarkers can reconcile; the
      // subsequent onFeedsChange will bump feedsVersion and re-run this effect
      // with the corrected bitmap.
      if (!unread.size && appState.feedEngine.getUnreadCount(selectedFeed) > 0)
        appState.feedEngine.pollNow().catch(() => undefined);
      // Open in the unread-only view whenever there are unread posts — also in
      // browse ('a') mode, where read posts between unread ones stay hidden until
      // the user picks "Show all posts". A fully-read browse feed opens showing all.
      effectiveUnreadOnly = !paused && (appState.feedEngine.getShowRead(selectedFeed) == 'u' || unread.size > 0);
      setShowUnreadOnly(effectiveUnreadOnly);
    } else {
      // Same feed: extend the snapshot with any posts the engine now considers
      // unread that aren't already in it. We never REMOVE from the snapshot —
      // posts you've started reading stay visible even after they auto-mark-read
      // so the list doesn't jump out from under you. But new posts (from polls)
      // and posts marked unread elsewhere must be ADDED, or once the snapshot
      // drifts from the current unread set the user gets stuck on "All caught
      // up" while the engine knows there are unread posts.
      const readSet = appState.feedEngine.getReadForFeed(selectedFeed);
      const snap = unreadSnapshotRef.current;
      for (let i = startPost; i < feed.postCount; i++)
        if (!readSet.has(i)) snap.add(i);
    }

    // Determine whether to show filtered (unread) or all posts.
    // In unread-only mode, keep the filtered view even when there are zero unread posts,
    // so the HeadlineList stays hidden until the user explicitly chooses "Show all posts".
    const showFiltered = effectiveUnreadOnly && !showAllTemporarily;
    let posts: [string, number][];
    if (showFiltered) {
      const unread = unreadSnapshotRef.current;
      posts = [];
      if (sortNewestFirst) {
        for (let i = feed.postCount - 1; i >= startPost; i--)
          if (unread.has(i)) posts.push([feed.id, i]);
      } else {
        for (let i = startPost; i < feed.postCount; i++)
          if (unread.has(i)) posts.push([feed.id, i]);
      }
    } else {
      const all: [string, number][] = sortNewestFirst
        ? Array.from({ length: count }, (_, i) => [feed.id, feed.postCount - 1 - i])
        : Array.from({ length: count }, (_, i) => [feed.id, startPost + i]);
      // Browse-only ('a') feeds with unread posts: initially show only down to the
      // last unread post in display order; older read posts are hidden behind a
      // "Show more" button. When everything's been read, show all (the else cases).
      let truncated = false;
      if (!effectiveUnreadOnly && !showAllTemporarily && !paused) {
        const unread = unreadSnapshotRef.current;
        let lastUnreadPos = -1;
        if (unread.size)
          for (let p = 0; p < all.length; p++)
            if (unread.has(all[p][1])) lastUnreadPos = p;
        truncated = lastUnreadPos >= 0 && lastUnreadPos < all.length - 1;
        posts = truncated ? all.slice(0, lastUnreadPos + 1) : all;
      } else
        posts = all;
      setBrowseTruncated(truncated);
    }
    if (showFiltered) setBrowseTruncated(false);
    setPostList(prev => {
      // Same view, only data changed: don't pull new posts into the list the user is reading.
      if (!viewChanged) return freezePostList(prev, posts);
      if (prev.length == posts.length && prev.every(([f, n], i) => f == posts[i][0] && n == posts[i][1]))
        return prev;
      return posts;
    });

    // TEMP DEBUG (remove): smoking gun for the "empty feed despite unread" bug.
    // Fires only when we emit an empty filtered list while the engine still
    // believes this feed has unread posts — exactly the symptom. The fields
    // below distinguish the theories:
    //  - newestRead >= barePostCount   → unread posts live above our scan range
    //  - feedChanged:true              → broke on the first (frozen) snapshot
    //  - engineState UPDATING          → coincided with a background poll
    if (showFiltered && !posts.length && appState.feedEngine.getUnreadCount(selectedFeed) > 0) {
      const marker = appState.feedEngine.getFeedMarker(selectedFeed);
      console.warn('[EMPTY-FEED-BUG] empty unread list but engine reports unread>0', {
        feed: selectedFeed,
        engineUnread: appState.feedEngine.getUnreadCount(selectedFeed),
        snapshotSize: unreadSnapshotRef.current.size,
        readSetSize: appState.feedEngine.getReadForFeed(selectedFeed).size,
        barePostCount: feed.postCount,
        startPost,
        newestRead: marker?.newestRead,
        markerUnreadCount: marker?.unreadCount,
        showUnreadOnly,
        effectiveUnreadOnly,
        showAllTemporarily,
        feedChanged,
        engineState: appState.feedEngine.getState(),
      });
    }

    // Hash restore for scroll position (only on initial load)
    const hash = initialHashRef.current.replace('#', '');
    initialHashRef.current = '';
    const hashPostNum = hash ? parseInt(hash, 10) : NaN;
    if (!isNaN(hashPostNum)) {
      const idx = posts.findIndex(([, pn]) => pn == hashPostNum);
      if (idx >= 0) {
        setCurrentPostIndex(idx);
        setScrollToPost([feed.id, hashPostNum]);
      }
    }
  }, [selectedFeed, selectedFolder, sortNewestFirst, showUnreadOnly, showAllTemporarily, feedsVersion, feedsStillLoading, appState.feedEngine, isReadOnlyPreview, unsubFeedInfo]);

  // Self-heal watchdog for the empty-feed bug: if the unread-only view of a
  // regular feed sits on the empty caught-up state while the engine reports
  // unread posts, force a rebuild — the snapshot extension above picks up
  // whatever the marker engine knows NOW (every stuck-state dump so far showed
  // locally-consistent marker state that a rebuild would have displayed) — and
  // poll so a still-drifted bitmap gets reconciled. The first attempt fires at
  // 1.2s (deliberately after PostView's 0.9s STUCK detector, so that diagnostic
  // keeps logging first) because the user is staring at a spinner until we heal;
  // later attempts back off to 3s. Capped per feed selection so a wrong engine
  // count can't loop.
  const stuckHealAttemptsRef = useRef(0);
  useEffect(() => { stuckHealAttemptsRef.current = 0; }, [selectedFeed]);
  useEffect(() => {
    if (isSpecialFeed || feedsStillLoading || postList.length || !showUnreadOnly || showAllTemporarily) return;
    if (appState.feedEngine.getUnreadCount(selectedFeed) <= 0) return;
    if (stuckHealAttemptsRef.current >= 3) return;
    const timer = setTimeout(() => {
      if (appState.feedEngine.getUnreadCount(selectedFeed) <= 0) return;
      const attempt = ++stuckHealAttemptsRef.current;
      console.warn(`[EMPTY-FEED-BUG] watchdog: forcing rebuild + poll for feed ${selectedFeed} (attempt ${attempt}/3)`);
      appState.feedEngine.pollNow().catch(() => undefined);
      setFeedsVersion(v => v + 1);
    }, stuckHealAttemptsRef.current ? 3000 : 1200);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, isSpecialFeed, feedsStillLoading, postList, showUnreadOnly, showAllTemporarily, feedsVersion, appState.feedEngine]);

  // Compute whether all-items has more unread posts beyond the displayed 1000
  const hasMoreUnread = useMemo(() => {
    if (selectedFeed != 'all-items') return false;
    const sortOrder = sortNewestFirst ? 'newestFirst' : 'oldestFirst';
    return appState.feedEngine.getUnreadFeedInfo(sortOrder as 'newestFirst' | 'oldestFirst', selectedFolder)?.hasMore ?? false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, selectedFolder, sortNewestFirst, feedsVersion, appState.feedEngine]);

  const handleMarkAllAndLoadMore = useCallback(() => {
    for (const [feedId, postNumber] of postListRef.current)
      if (!appState.feedEngine.isPostRead(feedId, postNumber))
        appState.feedEngine.markPostRead(feedId, postNumber);
    setUnreadRefreshCounter(v => v + 1);
  }, [appState.feedEngine]);

  // Toggle handlers - just update feedEngine (renderers manage their own DOM).
  // A refused save (anon saved-posts cap) reverts the renderers' optimistic flip and
  // shows the explanatory toast instead.
  const handleToggleStar = useCallback((feedId: string, postNumber: number, isStarred: boolean) => {
    const value = isStarred
      ? (appState.feedEngine.unstarPost(feedId, postNumber), false)
      : appState.feedEngine.starPost(feedId, postNumber);
    if (!isStarred && !value)
      postViewRef.current?.showSavedLimitToast('star');
    headlineListRef.current?.updatePostStatus(feedId, postNumber, 'star', value);
    postViewRef.current?.updatePostStatus(feedId, postNumber, 'star', value);
  }, [appState.feedEngine]);

  const handleToggleReadLater = useCallback((feedId: string, postNumber: number, isReadLater: boolean) => {
    const value = isReadLater
      ? (appState.feedEngine.unmarkReadLater(feedId, postNumber), false)
      : appState.feedEngine.markReadLater(feedId, postNumber);
    if (!isReadLater && !value)
      postViewRef.current?.showSavedLimitToast('readLater');
    headlineListRef.current?.updatePostStatus(feedId, postNumber, 'readLater', value);
    postViewRef.current?.updatePostStatus(feedId, postNumber, 'readLater', value);
  }, [appState.feedEngine]);

  const handleToggleRead = useCallback((feedId: string, postNumber: number, isRead: boolean) => {
    if (isRead)
      appState.feedEngine.markPostUnread(feedId, postNumber);
    else
      appState.feedEngine.markPostRead(feedId, postNumber);
    headlineListRef.current?.updatePostStatus(feedId, postNumber, 'read', !isRead);
    postViewRef.current?.updatePostStatus(feedId, postNumber, 'read', !isRead);
  }, [appState.feedEngine]);

  // Called by HeadlineRenderer auto-mark-read: value is the new state (always true)
  const handleHeadlineMark = useCallback((feedId: string, postNumber: number, _type: 'read', value: boolean) => {
    if (value)
      appState.feedEngine.markPostRead(feedId, postNumber);
    else
      appState.feedEngine.markPostUnread(feedId, postNumber);
    postViewRef.current?.updatePostStatus(feedId, postNumber, 'read', value);
  }, [appState.feedEngine]);

  const handleMarkAllRead = useCallback(() => {
    if (!selectedFeed)
      return;

    if (selectedFeed == 'all-items') {
      for (const [feedId, postNumber] of postListRef.current)
        if (!appState.feedEngine.isPostRead(feedId, postNumber))
          appState.feedEngine.markPostRead(feedId, postNumber);
      setPostList([]);
      return;
    }

    if (selectedFeed == 'read-later' || selectedFeed == 'starred') {
      for (const [feedId, postNumber] of postListRef.current) {
        if (appState.feedEngine.isPostRead(feedId, postNumber)) continue;
        appState.feedEngine.markPostRead(feedId, postNumber);
        headlineListRef.current?.updatePostStatus(feedId, postNumber, 'read', true);
      }
      return;
    }

    // Regular feed: prior returns already handled the special-feed cases.
    if (selectedFeed) {
      appState.feedEngine.markAllRead(selectedFeed);
      headlineListRef.current?.markAllRead();
      if (showUnreadOnlyRef.current) {
        // Clear the snapshot too — the layout effect's extend-on-rebuild would
        // otherwise immediately re-populate postList with the just-read posts.
        unreadSnapshotRef.current = new Set();
        setPostList([]);
      }
    }
  }, [selectedFeed, appState.feedEngine]);

  // Bring back a post list that a prior "Mark all read" emptied (undo). Re-seed the
  // unread snapshot for regular feeds so the rebuild effect (which fires when the engine
  // re-marks those posts unread) keeps them instead of filtering them straight back out.
  const handleRestorePostList = useCallback((restored: [string, number][]) => {
    if (selectedFeed && !isSpecialFeed) {
      const snap = unreadSnapshotRef.current;
      for (const [fid, pn] of restored) if (fid == selectedFeed) snap.add(pn);
    }
    // The undo already restored the engine read state; mirror it into the headline list,
    // which (unlike the post view) doesn't rebuild itself from the engine.
    for (const [fid, pn] of restored)
      headlineListRef.current?.updatePostStatus(fid, pn, 'read', appState.feedEngine.isPostRead(fid, pn));
    setPostList(restored);
  }, [selectedFeed, isSpecialFeed, appState.feedEngine]);

const handleSetShowRead = useCallback((showRead: 'u' | 'a') => {
    if (selectedFeed && !isSpecialFeed)
      appState.feedEngine.setShowRead(selectedFeed, showRead);
    // Switching the tracking mode doesn't reveal read posts: the unread-only
    // filter stays on while the feed has unread posts, in both modes.
    setShowUnreadOnly(showRead == 'u' || (!isSpecialFeed && appState.feedEngine.getUnreadCount(selectedFeed) > 0));
    setShowAllTemporarily(false);
  }, [selectedFeed, isSpecialFeed, appState.feedEngine]);

  const handleShowAll = useCallback(() => {
    setShowAllTemporarily(true);
  }, []);

  const handleRefresh = useCallback(() => {
    if (selectedFeed == 'all-items') {
      setUnreadRefreshCounter(v => v + 1);
      return;
    }
    // An explicit refresh rebuilds the list from scratch, including posts that arrived while reading.
    prevSelectedFeedRef.current = '';
    prevViewKeyRef.current = '';
    setFeedsVersion(v => v + 1);
  }, [selectedFeed]);

  const handleEndOfUnread = useCallback(() => {
    if (selectedFeed && !isSpecialFeed) {
      appState.feedEngine.markAllRead(selectedFeed);
      headlineListRef.current?.markAllRead();
    }
    // Clear snapshot too so the layout effect's extend-on-rebuild doesn't
    // immediately re-populate postList with the just-read posts.
    unreadSnapshotRef.current = new Set();
    setPostList([]);
  }, [selectedFeed, isSpecialFeed, appState.feedEngine]);

  const handleMarkOlderThanRead = useCallback((days: number) => {
    headlineListRef.current?.markOlderThanRead(days);
    if (selectedFeed && !isSpecialFeed) {
      if (showUnreadOnlyRef.current) {
        const remaining = postListRef.current.filter(([fid, pn]) => !appState.feedEngine.isPostRead(fid, pn));
        const remainingSet = new Set(remaining.filter(([fid]) => fid == selectedFeed).map(([, pn]) => pn));
        unreadSnapshotRef.current = remainingSet;
        setPostList(remaining);
      } else if (postListRef.current.length) {
        const [fid, pn] = postListRef.current[0];
        setScrollToPost([fid, pn]);
      }
      setCurrentPostIndex(0);
    }
  }, [selectedFeed, isSpecialFeed, appState.feedEngine]);

  // Compute next/prev unread feeds for navigation (matching LeftSidebar order)
  const getOrderedFeedIds = useCallback(() => {
    const feeds = appState.feedEngine.getFeeds();
    const folders = appState.feedEngine.getFolders();
    const feedById = new Map(feeds.map(f => [f.id, f]));
    const namedFolders = Object.entries(folders).filter(([k]) => k !== '');
    const feedsInNamedFolders = new Set(namedFolders.flatMap(([, ids]) => ids));
    const unfiledOrder = folders[''] ?? [];
    const orderedUnfiled = unfiledOrder.filter(id => feedById.has(id));
    const remainingUnfiled = feeds.filter(f => !feedsInNamedFolders.has(f.id) && !unfiledOrder.includes(f.id)).map(f => f.id);

    const ordered: string[] = [];
    namedFolders.sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    for (const [, feedIds] of namedFolders)
      for (const id of feedIds) if (feedById.has(id)) ordered.push(id);
    ordered.push(...orderedUnfiled, ...remainingUnfiled);
    return ordered;
  }, [appState.feedEngine]);

  const nextUnreadFeed = useMemo(() => {
    // Folder unread view (/a/new/<folder>): the next stop after finishing the folder
    // is the next folder with unread posts in sidebar order (wrapping around) — entered
    // directly at its single unread feed when it only has one — and after the last
    // folder any unfiled feed with unread posts. Undefined (=> Home) only when nothing
    // outside this folder is left unread.
    if (selectedFeed == 'all-items' && selectedFolder != undefined) {
      const engine = appState.feedEngine;
      const feeds = engine.getFeeds();
      const feedById = new Map(feeds.map(f => [f.id, f]));
      const folders = engine.getFolders();
      const namedFolders = Object.entries(folders).filter(([k]) => k != '')
        .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      const feedsInNamedFolders = new Set(namedFolders.flatMap(([, ids]) => ids));
      const unfiledOrder = folders[''] ?? [];
      const unfiled = [
        ...unfiledOrder.filter(id => feedById.has(id)),
        ...feeds.filter(f => !feedsInNamedFolders.has(f.id) && !unfiledOrder.includes(f.id)).map(f => f.id),
      ];
      // Sidebar-order groups: each named folder, then the unfiled feeds as one last group.
      const groups: [string | undefined, string[]][] = [...namedFolders, [undefined, unfiled]];
      const idx = groups.findIndex(([name]) => name == selectedFolder);
      for (let off = 1; off <= groups.length; off++) {
        const [name, ids] = groups[(idx + off + groups.length) % groups.length];
        if (name == selectedFolder) continue;
        const unreadIds = ids.filter(id => feedById.has(id) && engine.getUnreadCount(id) > 0);
        if (!unreadIds.length) continue;
        // Unfiled feeds are entered one by one; a folder with a single unread feed is
        // entered directly at that feed.
        if (name == undefined || unreadIds.length == 1) {
          const f = feedById.get(unreadIds[0]);
          return { id: unreadIds[0], name: f?.shortTitle || f?.title || 'Feed' };
        }
        return { id: `folder:${name}`, name, isFolder: true };
      }
      return undefined;
    }
    if (!selectedFeed || isSpecialFeed) return undefined;
    const ordered = getOrderedFeedIds();
    const idx = ordered.indexOf(selectedFeed);
    if (idx < 0) return undefined;

    // Cyclic search for the next feed with unread posts: scan from just below the
    // current feed down to the end, then wrap around to the top. This always lands
    // on the next possible feed, and when every unread feed is above the current
    // one it returns the first feed in the list. Undefined means no other feed has
    // unread posts (callers then offer a Home action instead).
    for (let off = 1; off < ordered.length; off++) {
      const id = ordered[(idx + off) % ordered.length];
      if (appState.feedEngine.getUnreadCount(id) <= 0) continue;
      const f = appState.feedEngine.getFeed(id);
      return { id, name: f?.shortTitle || f?.title || 'Feed' };
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, selectedFolder, isSpecialFeed, getOrderedFeedIds, feedsVersion, appState.feedEngine]);
  const nextUnreadFeedRef = useRef(nextUnreadFeed);
  nextUnreadFeedRef.current = nextUnreadFeed;

  // Keyboard shortcuts (Google Reader style) - use refs to avoid stale closures
  useEffect(() => {
    // Helper to check if user is typing in an input field
    const isTyping = () => {
      const activeEl = document.activeElement;
      if (!activeEl) return false;
      const tagName = activeEl.tagName.toLowerCase();
      return tagName == 'input' || tagName == 'textarea' || (activeEl as HTMLElement).isContentEditable;
    };

    const goToNextPost = () => {
      if (isTyping()) return false;
      markReaderKeysUsed();
      navNext();
      return true;
    };
    const goToPrevPost = () => {
      if (isTyping()) return false;
      markReaderKeysUsed();
      navPrev();
      return true;
    };

    // Mark all read the same way the header's "Mark all read" button does: when any
    // post is locked (keepUnread), skip the engine's bulk markAllRead (which would
    // clear the locks) and mark only the unlocked posts.
    const markAllReadKeepingLocks = () => {
      if (postViewRef.current?.hasLockedPosts())
        postViewRef.current.markAllReadUnlessLocked();
      else
        handleMarkAllRead();
    };

    const scrollPostPage = (direction: number) => {
      if (isTyping()) return false;
      // Modal dialogs don't block global handlers; leave the keys to an open modal
      if (Array.from(document.querySelectorAll<HTMLElement>('.flowFormModal')).some(el => el.offsetParent || el.matches(':modal')))
        return false;
      const container = Array.from(document.querySelectorAll<HTMLElement>('.postsContainer'))
        .find(el => el.offsetParent);
      if (!container) return false;
      // Already at the bottom: focus Mark all read / Next Feed / Home, like 'j' at the last post
      if (direction > 0 && container.scrollHeight - container.clientHeight - container.scrollTop <= 8) {
        focusEndOfListAction();
        return true;
      }
      container.dispatchEvent(new Event('cancelscroll'));
      container.scrollBy({ top: container.clientHeight * 0.9 * direction, behavior: 'smooth' });
      return true;
    };

    registerKeyHandler('main-reader', {
      'Enter': () => {
        // When nothing is focused, treat Enter as clicking the currently selected
        // headline. Useful in narrow tablet mode (LeftSidebar + HeadlineList only) to
        // open the post in PostView without reaching for the mouse.
        if (isTyping()) return false;
        const ae = document.activeElement;
        if (ae && ae != document.body && ae.tagName != 'HTML') return false;
        const card = document.querySelector<HTMLElement>('.headlineList .headlineItemCard.selected');
        if (!card?.offsetParent) return false;
        card.click();
        return true;
      },
      'j': goToNextPost,
      'k': goToPrevPost,
      'Space': () => { // Scroll post view with Space/Shift+Space
        if (isTyping()) return false;
        const container = Array.from(document.querySelectorAll<HTMLElement>('.postsContainer'))
          .find(el => el.offsetParent) ?? null;
        if (container) {
          const pageStep = container.clientHeight * 0.9;
          const remainingScroll = container.scrollHeight - container.clientHeight - container.scrollTop;
          if (remainingScroll <= 8) {
            focusEndOfListAction();
            return true;
          }
          if (remainingScroll <= pageStep + 8) {
            container.dispatchEvent(new Event('cancelscroll'));
            container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
            requestAnimationFrame(() => requestAnimationFrame(focusEndOfListAction));
            return true;
          }
          container.dispatchEvent(new Event('cancelscroll'));
          container.scrollBy({ top: pageStep, behavior: 'smooth' });
        }
        return true;
      },
      'Shift+Space': () => scrollPostPage(-1), // Scroll up with Shift+Space
      // PageUp/PageDown always page the post view, wherever focus is (sidebar, headline
      // list, header buttons...). Natively they'd only scroll the focused element's pane.
      'PageUp': () => scrollPostPage(-1),
      'PageDown': () => scrollPostPage(1),
      'n': goToNextPost,
      'p': goToPrevPost,
      's': () => { // Star/unstar with 's' key
        if (isTyping()) return false;
        const posts = postListRef.current;
        const [feedId, postNumber] = posts[resolveCurrentIndex()] || [];
        if (feedId) {
          const starred = appState.feedEngine.getStarredForFeed(feedId).has(postNumber);
          handleToggleStar(feedId, postNumber, starred);
        }
        return true;
      },
      'm': () => { // Lock / unlock the current post (keep unread) — same as its keep icon
        if (isTyping()) return false;
        const posts = postListRef.current;
        const [feedId, postNumber] = posts[resolveCurrentIndex()] || [];
        if (feedId) postViewRef.current?.toggleKeepUnread(feedId, postNumber);
        return true;
      },
      'r': () => { // Toggle read later
        if (isTyping()) return false;
        const posts = postListRef.current;
        const [feedId, postNumber] = posts[resolveCurrentIndex()] || [];
        if (feedId) {
          const readLater = appState.feedEngine.getReadLaterForFeed(feedId).has(postNumber);
          handleToggleReadLater(feedId, postNumber, readLater);
        }
        return true;
      },
      'a': () => { // Mark all as read (keeping locks, like the header button)
        if (isTyping()) return false;
        const sf = selectedFeedRef.current;
        if (sf == 'starred' || sf == 'read-later') return true;
        markAllReadKeepingLocks();
        return true;
      },
      'Alt+a': () => {
        const sf = selectedFeedRef.current;
        if (sf == 'starred' || sf == 'read-later') return true;
        markAllReadKeepingLocks();
        return true;
      },
      'Alt+k': () => { // Keep all unread (only feeds that use read markers)
        const sf = selectedFeedRef.current;
        if (sf == 'starred' || sf == 'read-later') return true;
        postViewRef.current?.keepAllUnread();
        return true;
      },
      'Alt+s': () => { // Skip feed: keep all unread, then next feed / home (same as the Skip button)
        const sf = selectedFeedRef.current;
        if (sf == 'starred' || sf == 'read-later') return true;
        postViewRef.current?.keepAllUnread();
        handleSelectFeed(nextUnreadFeedRef.current?.id ?? 'home');
        return true;
      },
      'Alt+n': () => { // Go to the next unread feed (same as the Next Feed button)
        const next = nextUnreadFeedRef.current;
        if (!next) { // No next feed (e.g. special lists like read-later) — go home
          goTo(routerCtx, '/a/home');
          return true;
        }
        postViewRef.current?.markAllReadUnlessLocked();
        handleSelectFeed(next.id);
        return true;
      },
      'h': () => { // Go to the home page
        if (isTyping()) return false;
        goTo(routerCtx, '/a/home');
        return true;
      },
      ...Object.fromEntries(ADD_FEED_KEYS.map(k => [k, () => { // Go to Add Feed
        if (isTyping()) return false;
        goTo(routerCtx, '/a/add');
        return true;
      }])),
    });
    return () => unregisterKeyHandler('main-reader');
  }, [navNext, navPrev, focusEndOfListAction, resolveCurrentIndex, handleToggleRead, handleToggleReadLater, handleToggleStar, handleMarkAllRead, handleSetShowRead, handleSelectFeed, appState.feedEngine, routerCtx]);

  const currentFeedName = useMemo(() => {
    if (selectedFeed == 'all-items') return selectedFolder ?? 'Unread Posts';
    if (selectedFeed == 'read-later') return 'Read Later';
    if (selectedFeed == 'starred') return 'Starred';
    const feed = appState.feedEngine.getFeed(selectedFeed);
    // Until an unfollowed feed's info arrives, use what the tile it was opened from knew.
    return feed ? (feed.shortTitle || feed.title) : appState.feedEngine.getFeedHeaderHint(selectedFeed)?.title ?? 'Feed';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, selectedFolder, appState.feedEngine, feedsVersion]);

  const currentFeedIsPrivate = useMemo(() => {
    const feed = appState.feedEngine.getFeed(selectedFeed);
    return feed?.feedStatus == 'PRIVATE';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, appState.feedEngine, feedsVersion]);

  const currentFeedIsNewsletter = useMemo(() => {
    const feed = appState.feedEngine.getFeed(selectedFeed);
    return feed?.feedStatus == 'NEWSLETTER';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, appState.feedEngine, feedsVersion]);

  const currentFeedIssue = useMemo(() => {
    const feed = appState.feedEngine.getFeed(selectedFeed);
    return { fetchErrorCount: feed?.fetchErrorCount, fetchFailingSinceAt: feed?.fetchFailingSinceAt, feedStatus: feed?.feedStatus };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, appState.feedEngine, feedsVersion]);

  const currentNewsletterEmail = useMemo(() => {
    const feed = appState.feedEngine.getFeed(selectedFeed);
    return feed?.feedStatus == 'NEWSLETTER' ? (feed.url ?? '').replace(/^mailto:/i, '') : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, appState.feedEngine, feedsVersion]);

  // Deleting a newsletter feed is irreversible (its posts are wiped and the sender is
  // unsubscribed), so confirm first — unlike unfollowing a normal feed.
  const showDeleteNewsletterConfirmation = useConfirmationModal<string>({
    title: 'Delete newsletter feed',
    confirmContent: 'Delete',
    dismissContent: 'Cancel',
    sidewayFocus: true,
    children: 'This newsletter feed and all its posts will be permanently deleted and cannot be restored. The newsletter will be unsubscribed.',
    onConfirm: (feedId: string) => appState.feedEngine.unsubscribe(feedId),
  });

  const currentFeedFavicon = useMemo(() => {
    if (isSpecialFeed) return undefined;
    const feed = appState.feedEngine.getFeed(selectedFeed);
    return feed ? getFeedIconUrl(feed, 48) : appState.feedEngine.getFeedHeaderHint(selectedFeed)?.iconUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, isSpecialFeed, appState.feedEngine, feedsVersion]);

  const currentFeedCircularIcon = useMemo(() => {
    if (isSpecialFeed) return undefined;
    return (appState.feedEngine.getFeed(selectedFeed) ?? appState.feedEngine.getFeedHeaderHint(selectedFeed))?.circularIcon;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, isSpecialFeed, appState.feedEngine, feedsVersion]);

  // The feed's persisted tracking mode for the header menu. Distinct from
  // showUnreadOnly: browse ('a') feeds also open unread-filtered now.
  const showReadMode = useMemo(() => {
    if (isSpecialFeed) return undefined;
    return appState.feedEngine.getShowRead(selectedFeed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeed, isSpecialFeed, appState.feedEngine, feedsVersion, compactModeVersion]);

  const hideHeadlineList = (viewMode == 'full' && !isMobileWidth) && (
    (showUnreadOnly && !showAllTemporarily && !postList.length && !isSpecialFeed) ||
    (isSpecialFeed && !postList.length)
  );
  // Two-column mode: the HeadlineList column is shown next to the PostView.
  const headlineListVisible = viewMode == 'full' && !isMobileWidth && !hideHeadlineList && !isUnsubscribedFeed;
  // Read-only lists: special feeds and the preview of an unfollowed feed get no feed controls.
  const readOnlyList = isSpecialFeed || isReadOnlyPreview;
  // The FeedTile above the posts: always for an unfollowed feed's preview, by default for a paused
  // feed (it carries the pause controls, but can be hidden), on demand for any other followed one.
  const selectedPaused = !readOnlyList && appState.feedEngine.isFeedPaused(selectedFeed);
  const tileVisible = tileShownFeedId == selectedFeed || (selectedPaused && tileHiddenFeedId != selectedFeed);
  const tileFeed = isUnsubscribedFeed ? unsubFeedInfo
    : tileVisible ? appState.feedEngine.getFeed(selectedFeed) : undefined;
  const showTile = (show: boolean) => {
    setTileShownFeedId(show ? selectedFeed : undefined);
    setTileHiddenFeedId(show ? undefined : selectedFeed);
  };
  const tileSubscribed = !!tileFeed && appState.feedEngine.getFeeds().some(f => f.id == tileFeed.id);
  // The preview menu's Follow presses the tile's own Follow button, so both share one follow path
  // (quota check, folder question anchored at that button, error display).
  const followFromTile = () => {
    const button = document.querySelector<HTMLButtonElement>('.feedPreviewTile .feedTileActionButton');
    button?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    button?.click();
  };

  return (
    <ShortcutsContext.Provider value={READER_SHORTCUTS}>
    <FlowFormLeaveContext.Provider value={sidebarLeaveHandler}>
    <MainLayout
      selectedFeed={selectedFeed}
      onSelectFeed={handleSelectFeed}
      middle={!feedsStillLoading && !isUnsubscribedFeed && headlineListVisible ? ({ onArticleSelect, isMobile }) => {
        return (
          <HeadlineList
            // Rebuilt on pause/resume, like PostView's renderer, to pick up the changed read states.
            key={`${selectedFolder != undefined ? `${selectedFeed}|${selectedFolder}` : selectedFeed}${selectedPaused ? ':paused' : ''}`}
            ref={headlineListRef}
            posts={postList}
            startAt={currentPostIndex}
            onSelect={handleHeadlineSelect}
            onAllRead={isSpecialFeed && selectedFeed != 'all-items' ? () => {} : handleMarkAllRead}
            onMark={handleHeadlineMark}
            onArticleSelect={onArticleSelect}
            isMobile={isMobile}
            showUnreadOnly={isSpecialFeed ? false : showUnreadOnly}
            isSavedList={selectedFeed == 'starred' || selectedFeed == 'read-later'}
            isUnreadList={selectedFeed == 'all-items'}
            onEndOfUnread={isSpecialFeed ? undefined : handleEndOfUnread}
            onShowAll={isSpecialFeed ? undefined : handleShowAll}
            nextUnreadFeed={nextUnreadFeed}
            onNavigateToFeed={handleSelectFeed}
            overflowMessage={hasMoreUnread ? 'Showing first 1000 unread posts.' : undefined}
            onOverflowAction={hasMoreUnread ? handleMarkAllAndLoadMore : undefined}
            onLoadMore={hasMoreUnread ? handleMarkAllAndLoadMore : (browseTruncated ? handleShowAll : undefined)}
            loadMoreLabel={browseTruncated ? 'Show more' : undefined}
            loadMoreWithNextFeed={browseTruncated}
            onKeepAllUnread={() => postViewRef.current?.keepAllUnread()}
          />
        );
      } : undefined}
      right={engineState == 'ERROR' ? () => (
        <FeedLoadError />
      ) : feedsStillLoading ? () => (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Spinner spinning />
        </div>
      ) : ({ showExpandButton, onExpand, isMobile }) => (
        <PostView
          key={selectedFolder != undefined ? `${selectedFeed}|${selectedFolder}` : selectedFeed}
          ref={postViewRef}
          posts={postList}
          feedName={currentFeedName}
          feedFaviconUrl={currentFeedFavicon}
          feedCircularIcon={currentFeedCircularIcon}
          isPrivateFeed={currentFeedIsPrivate}
          isNewsletterFeed={currentFeedIsNewsletter}
          newsletterEmail={currentNewsletterEmail}
          feedFetchErrorCount={currentFeedIssue.fetchErrorCount}
          feedFailingSinceAt={currentFeedIssue.fetchFailingSinceAt}
          feedFeedStatus={currentFeedIssue.feedStatus}
          showExpandButton={showExpandButton}
          onExpand={onExpand}
          isMobile={isMobile}
          currentPostIndex={currentPostIndex}
          onNextPost={navNext}
          onPrevPost={navPrev}
          onToggleStar={handleToggleStar}
          onToggleReadLater={handleToggleReadLater}
          onToggleRead={handleToggleRead}
          onMarkAllRead={handleMarkAllRead}
          onRestorePostList={handleRestorePostList}
          onKeepAllUnread={() => headlineListRef.current?.keepAllUnread()}
          onMarkOlderThanRead={readOnlyList ? undefined : handleMarkOlderThanRead}
          sortNewestFirst={isReadOnlyPreview || sortNewestFirst}
          onSortChange={isReadOnlyPreview ? undefined : handleSortChange}
          showUnreadOnly={readOnlyList ? false : showUnreadOnly}
          showRead={showReadMode}
          onSetShowRead={readOnlyList ? undefined : handleSetShowRead}
          onEndOfUnread={readOnlyList ? undefined : handleEndOfUnread}
          onShowAll={readOnlyList ? undefined : handleShowAll}
          onRefresh={handleRefresh}
          nextUnreadFeed={nextUnreadFeed}
          unreadFolder={selectedFeed == 'all-items' ? selectedFolder : undefined}
          onNavigateToFeed={handleSelectFeed}
          scrollToPost={scrollToPost}
          onVisiblePostChange={handleVisiblePostChange}
          viewMode={effectiveViewMode}
          onCycleViewMode={handleCycleViewMode}
          onSetViewMode={handleSetViewMode}
          feedConfigKey={readOnlyList ? undefined : selectedFeed}
          specialListType={isReadOnlyPreview ? 'preview' : selectedFeed == 'starred' ? 'starred' : selectedFeed == 'read-later' ? 'read-later' : selectedFeed == 'all-items' ? 'all-items' : undefined}
          loading={isReadOnlyPreview ? !unsubFeedInfo : (allItemsLoading || appState.feedEngine.getState() == 'OFF' || appState.feedEngine.getState() == 'LOADING')}
          feedLoadingPosts={feedLoadingPosts}
          hasMoreUnread={!isReadOnlyPreview && hasMoreUnread}
          onMarkAllAndLoadMore={!isReadOnlyPreview && hasMoreUnread ? handleMarkAllAndLoadMore : undefined}
          onLoadMore={isReadOnlyPreview ? undefined : hasMoreUnread ? handleMarkAllAndLoadMore : (browseTruncated ? handleShowAll : undefined)}
          loadMoreLabel={browseTruncated ? 'Show more' : undefined}
          loadMoreWithNextFeed={browseTruncated}
          onUnfollow={!readOnlyList && feedIdFromUrl ? () => currentFeedIsNewsletter ? showDeleteNewsletterConfirmation({ onConfirmArg: feedIdFromUrl }) : appState.feedEngine.unsubscribe(feedIdFromUrl) : undefined}
          feedCategory={feedIdFromUrl ? appState.feedEngine.getFeed(feedIdFromUrl)?.category : undefined}
          isNavigatingAway={pendingFeedNavigation != null && pendingFeedNavigation != selectedFeed}
          onBack={handleBack}
          onFeedNameClick={readOnlyList || isUnsubscribedFeed ? undefined : () => showTile(!tileVisible)}
          onShowFeedTile={readOnlyList || isUnsubscribedFeed ? undefined : () => showTile(true)}
          onFollow={isReadOnlyPreview && tileFeed && !tileSubscribed ? followFromTile : undefined}
          previewFeedId={isReadOnlyPreview ? feedIdFromUrl : undefined}
          listHeader={tileFeed ? (
            <div className="feedPreviewTile">
              <FeedTile
                feed={tileFeed}
                subscribed={tileSubscribed}
                onAction={() => setFeedsVersion(v => v + 1)}
                hideView
                showPrivacyNote={true}
                isAdmin={isSuperuser(appState.state.authData?.selectedMembership?.capabilities)}
                onQuotaExceeded={() => setShowQuotaDialog(true)}
                extraBadges={tileSubscribed ? <FeedTileSettings feedId={tileFeed.id}
                  showRead={showReadMode} onSetShowRead={handleSetShowRead}
                  sortNewestFirst={sortNewestFirst} onSortChange={handleSortChange} /> : undefined}
                paused={tileSubscribed && selectedPaused}
              >
                {tileSubscribed && selectedPaused && <PauseControls feedId={tileFeed.id} />}
              </FeedTile>
              <FeedQuotaDialog show={showQuotaDialog} onClose={() => setShowQuotaDialog(false)} />
            </div>
          ) : undefined}
        />
      )}
      showMiddle={!isMobileWidth}
    />
    </FlowFormLeaveContext.Provider>
    </ShortcutsContext.Provider>
  );
};

export default Main;
